import { nextRowMutationIso } from "./row-mutation-clock.mjs";

export const DELETION_JOURNAL_DOMAINS = Object.freeze({
  todos: "todos",
  savedArticles: "saved_articles",
});

function normalizedText(value, maximumLength = 512) {
  const text = String(value || "").trim();
  return text && text.length <= maximumLength ? text : "";
}

function normalizedTimestamp(value) {
  const parsed = Date.parse(String(value || ""));
  return Number.isFinite(parsed) ? new Date(parsed).toISOString() : "";
}

function newMutationId(prefix) {
  try {
    return `${prefix}-${crypto.randomUUID()}`;
  } catch {
    return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }
}

function itemIdForDomain(domain, value) {
  return domain === DELETION_JOURNAL_DOMAINS.todos
    ? normalizedText(value?.clientId)
    : normalizedText(value?.articleId, 4_096);
}

function normalizeEntry(domain, slot, rawValue) {
  try {
    const value = JSON.parse(String(rawValue || ""));
    const mutationId = normalizedText(value?.mutationId, 240);
    const deletedAt = normalizedTimestamp(value?.deletedAt);
    const itemId = itemIdForDomain(domain, value);
    if (!mutationId || mutationId !== slot || !deletedAt || !itemId) return null;
    return {
      domain,
      slot,
      mutationId,
      deletedAt,
      ...(domain === DELETION_JOURNAL_DOMAINS.todos
        ? { clientId: itemId }
        : { articleId: itemId }),
    };
  } catch {
    return null;
  }
}

function entriesForDomain(io, domain) {
  const listed = io?.list?.(domain);
  const rows = Array.isArray(listed) ? listed : [];
  return rows
    .map((row) => normalizeEntry(domain, normalizedText(row?.slot, 240), row?.value))
    .filter(Boolean);
}

export function pendingDeletionJournal(io) {
  return {
    deletedTodos: entriesForDomain(io, DELETION_JOURNAL_DOMAINS.todos).map(
      ({ clientId, mutationId, deletedAt }) => ({ clientId, mutationId, deletedAt }),
    ),
    deletedSavedArticles: entriesForDomain(
      io,
      DELETION_JOURNAL_DOMAINS.savedArticles,
    ).map(({ articleId, mutationId, deletedAt }) => ({
      articleId,
      mutationId,
      deletedAt,
    })),
  };
}

export function queueDeletionJournalEntry(
  io,
  domain,
  itemId,
  {
    mutationId = newMutationId(
      domain === DELETION_JOURNAL_DOMAINS.todos ? "todo-delete" : "article-delete",
    ),
    deletedAt,
    observedUpdatedAt,
    nowEpochMs = Date.now(),
  } = {},
) {
  const id = normalizedText(
    itemId,
    domain === DELETION_JOURNAL_DOMAINS.savedArticles ? 4_096 : 512,
  );
  const normalizedMutationId = normalizedText(mutationId, 240);
  const normalizedDeletedAt = normalizedTimestamp(
    deletedAt == null
      ? nextRowMutationIso(observedUpdatedAt, nowEpochMs)
      : deletedAt,
  );
  if (
    !id ||
    !normalizedMutationId ||
    !normalizedDeletedAt ||
    !Object.values(DELETION_JOURNAL_DOMAINS).includes(domain)
  ) {
    return null;
  }
  const entry = {
    mutationId: normalizedMutationId,
    deletedAt: normalizedDeletedAt,
    ...(domain === DELETION_JOURNAL_DOMAINS.todos
      ? { clientId: id }
      : { articleId: id }),
  };
  io.write(domain, normalizedMutationId, JSON.stringify(entry));
  return entry;
}

function activeRowVersions(rows, itemField) {
  const versions = new Map();
  for (const row of Array.isArray(rows) ? rows : []) {
    const itemId = normalizedText(
      row?.[itemField],
      itemField === "articleId" ? 4_096 : 512,
    );
    const updatedAt = normalizedTimestamp(row?.updatedAt);
    if (!itemId || !updatedAt) continue;
    versions.set(
      itemId,
      Math.max(versions.get(itemId) || 0, Date.parse(updatedAt)),
    );
  }
  return versions;
}

/**
 * A retained delete intent can encounter a newer concurrent remote edit. Once
 * that version has been observed, advance the same durable mutation beyond it
 * so the next retry is causally ordered instead of spinning forever.
 */
export function advanceDeletionJournalAgainstRows(
  io,
  todos,
  savedArticles,
  nowEpochMs = Date.now(),
) {
  const activeByDomain = {
    [DELETION_JOURNAL_DOMAINS.todos]: activeRowVersions(todos, "clientId"),
    [DELETION_JOURNAL_DOMAINS.savedArticles]: activeRowVersions(
      savedArticles,
      "articleId",
    ),
  };
  for (const domain of Object.values(DELETION_JOURNAL_DOMAINS)) {
    for (const entry of entriesForDomain(io, domain)) {
      const activeUpdatedAt =
        activeByDomain[domain].get(itemIdForDomain(domain, entry)) || 0;
      if (!activeUpdatedAt || activeUpdatedAt < Date.parse(entry.deletedAt)) {
        continue;
      }
      const updated = {
        mutationId: entry.mutationId,
        deletedAt: nextRowMutationIso(activeUpdatedAt, nowEpochMs),
        ...(domain === DELETION_JOURNAL_DOMAINS.todos
          ? { clientId: entry.clientId }
          : { articleId: entry.articleId }),
      };
      io.write(domain, entry.slot, JSON.stringify(updated));
    }
  }
}

export function cancelDeletionJournalItem(io, domain, itemId) {
  const id = normalizedText(
    itemId,
    domain === DELETION_JOURNAL_DOMAINS.savedArticles ? 4_096 : 512,
  );
  if (!id) return;
  for (const entry of entriesForDomain(io, domain)) {
    if (itemIdForDomain(domain, entry) === id) {
      io.remove(domain, entry.slot);
    }
  }
}

export function acknowledgeDeletionJournalMutations(io, mutationIds) {
  if (!Array.isArray(mutationIds) || mutationIds.length === 0) return;
  const acknowledged = new Set(
    mutationIds.map((value) => normalizedText(value, 240)).filter(Boolean),
  );
  for (const domain of Object.values(DELETION_JOURNAL_DOMAINS)) {
    for (const entry of entriesForDomain(io, domain)) {
      // Every mutation owns a distinct storage slot. Removing this exact slot
      // cannot erase a newer mutation written by another tab.
      if (acknowledged.has(entry.mutationId)) io.remove(domain, entry.slot);
    }
  }
}

function remoteDeletionVersions(rows, itemField) {
  const versions = new Map();
  const mutationIds = new Set();
  for (const row of Array.isArray(rows) ? rows : []) {
    const itemId = normalizedText(row?.[itemField], itemField === "articleId" ? 4_096 : 512);
    const mutationId = normalizedText(row?.mutationId, 240);
    const deletedAt = normalizedTimestamp(row?.deletedAt);
    if (mutationId) mutationIds.add(mutationId);
    if (!itemId || !deletedAt) continue;
    const epochMs = Date.parse(deletedAt);
    versions.set(itemId, Math.max(versions.get(itemId) || 0, epochMs));
  }
  return { versions, mutationIds };
}

export function reconcileDeletionJournal(
  io,
  deletedTodos,
  deletedSavedArticles,
) {
  const remoteByDomain = {
    [DELETION_JOURNAL_DOMAINS.todos]: remoteDeletionVersions(
      deletedTodos,
      "clientId",
    ),
    [DELETION_JOURNAL_DOMAINS.savedArticles]: remoteDeletionVersions(
      deletedSavedArticles,
      "articleId",
    ),
  };
  for (const domain of Object.values(DELETION_JOURNAL_DOMAINS)) {
    const remote = remoteByDomain[domain];
    for (const entry of entriesForDomain(io, domain)) {
      const remoteEpochMs =
        remote.versions.get(itemIdForDomain(domain, entry)) || 0;
      if (
        remote.mutationIds.has(entry.mutationId) ||
        (remoteEpochMs > 0 && Date.parse(entry.deletedAt) <= remoteEpochMs)
      ) {
        io.remove(domain, entry.slot);
      }
    }
  }
}
